import { forwardRef, useContext, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { toast } from 'react-toastify';
import { UNIT_CREATION_MODES } from './constants.js';
import { Box, IconButton } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { Grid } from '@mui/material';
//mui imports====
import useStyles from './styles';
import Tab from '@mui/material/Tab';
import TabPanel from '@mui/lab/TabPanel';
import { FvpConfigContext } from '../../../../Contexts/FvpConfigContext/index.jsx';
import { Modalv2Context } from '../../../../Contexts/Modalv2Context/index.jsx';
import { initialFvpSectionConfig } from '../../../../utilities/initialConfigs.js';
import InputConfig from '../../../../Components/InputConfig/index.jsx';
import CreateVideoSectionWithCopyExisting from './Components/CreateVideoSectionWithCopyExisting.jsx';
import CreateVideoSectionWithdefaultValues from './Components/CreateVideoSectionWithDefaultValues.jsx';

import { ACCESS_CONTROL_CONSTANTS } from '../../../../core/constants.js';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';

const { SYSTEMS } = ACCESS_CONTROL_CONSTANTS;

const CreateUnitModalContent = forwardRef(() => {
	const classes = useStyles();
	const { fvpConfig, updateFvpConfig } = useContext(FvpConfigContext);
	const videoSection = fvpConfig?.ads;
	const [id, setId] = useState('');
	const [name, setName] = useState('');
	const [createUnitMode, setCreateUnitMode] = useState(UNIT_CREATION_MODES.CREATE_NEW);
	const [selectedExistingVideoSection, setSelectedExistingVideoSection] = useState('');
	const { updateModalContent } = useContext(Modalv2Context);

	const createNewVideoSectionWithDefaultValues = () => {
		const clonedFvpConfig = structuredClone(fvpConfig);
		const clonedInitialFvpSectionConfig = structuredClone(initialFvpSectionConfig);

		clonedFvpConfig.ads[id] = {
			...clonedInitialFvpSectionConfig,
			videoSectionId: id,
			videoSectionName: name
		};

		updateFvpConfig(clonedFvpConfig);

		updateModalContent(null);
	};

	const createNewVideoSectionWithCopyExisting = () => {
		if (!selectedExistingVideoSection) {
			toast.error('Please select an existing unit', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}

		if (!fvpConfig.ads[selectedExistingVideoSection]) {
			toast.error('Something went wrong selecting the unit', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		const clonedFvpConfig = structuredClone(fvpConfig);

		clonedFvpConfig.ads[id] = {
			...clonedFvpConfig.ads[selectedExistingVideoSection],
			videoSectionId: id,
			videoSectionName: name
		};

		updateFvpConfig(clonedFvpConfig);
		updateModalContent(null);
	};

	const createVideoSection = () => {
		if (!id || !name) {
			toast.error('Id or name missing', { position: toast.POSITION.BOTTOM_RIGHT });
			return;
		}

		if (fvpConfig.ads[id]) {
			toast.error('Video Section Unit with this id already exists!!', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		if (createUnitMode === UNIT_CREATION_MODES.CREATE_NEW) createNewVideoSectionWithDefaultValues();
		else createNewVideoSectionWithCopyExisting();
	};

	const SettingsButton = useMemo(() => {
		return (
			<IconButton
				onClick={() => {
					const uuidString = uuid().toString();
					const newName = `FVP_${uuidString.substring(0, 5)}`;
					setId(uuidString);
					setName(newName);
				}}
			>
				<SettingsIcon />
			</IconButton>
		);
	}, [name, id]);

	const customDropDownIds = () => {
		return Object.keys(videoSection).map((videoSectionId) => ({
			label: `${fvpConfig.ads[videoSectionId].videoSectionName} (${videoSection[videoSectionId].videoSectionId})`,
			value: `${videoSection[videoSectionId].videoSectionId}`
		}));
	};

	return (
		<Grid container spacing={2} className={classes.modalContentStyle}>
			<Grid item xs={6} className={classes.modalContentItem}>
				<InputConfig
					system={SYSTEMS.FVP_PLAYER}
					isObjectEditor={false}
					label={'Video Section Id'}
					keyData={`CREATE_FVP_VIDEO_SECTION_ID_INPUT`}
					directValue={id}
					updateDirectValue={(updatedId) => setId(updatedId)}
					iconConfiguration={{ endAdornment: SettingsButton }}
				/>
			</Grid>
			<Grid item xs={6} className={classes.modalContentItem}>
				<InputConfig
					system={SYSTEMS.FVP_PLAYER}
					isObjectEditor={false}
					label={'Video Section Name'}
					keyData={`CREATE_FVP_VIDEO_SECTION_NAME_INPUT`}
					directValue={name}
					updateDirectValue={(updatedName) => setName(updatedName)}
				/>
			</Grid>

			<TabContext value={createUnitMode}>
				{videoSection && Object.keys(videoSection).length > 0 && (
					<Grid item xs={12} className={classes.modalContentItem}>
						<Box
							sx={{
								borderBottom: 1,
								borderColor: 'divider',
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								marginTop: '3rem'
							}}
						>
							<TabList
								onChange={(e, newVal) => {
									setCreateUnitMode(newVal);
								}}
							>
								<Tab label="Create With Default Values" value={UNIT_CREATION_MODES.CREATE_NEW} />
								<Tab label="Copy Existing Section" value={UNIT_CREATION_MODES.COPY_EXISTING} />
							</TabList>
						</Box>
					</Grid>
				)}

				<Grid item xs={12} className={classes.modalContentItem}>
					<TabPanel value={UNIT_CREATION_MODES.CREATE_NEW}>
						<CreateVideoSectionWithdefaultValues createVideoSection={createVideoSection} />
					</TabPanel>

					<TabPanel value={UNIT_CREATION_MODES.COPY_EXISTING}>
						<CreateVideoSectionWithCopyExisting
							dropDownList={customDropDownIds()}
							selectedExistingVideoSection={selectedExistingVideoSection}
							updateSelectedExistingVideoSection={setSelectedExistingVideoSection}
							createVideoSection={createVideoSection}
						/>
					</TabPanel>
				</Grid>
			</TabContext>
		</Grid>
	);
});

export default CreateUnitModalContent;
